import "moment/locale/fr";

import { CommonModule } from "@angular/common";
import { Component, inject, viewChild } from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { LayoutComponent } from "@jack-sparrow-libraries/ui-kit";
import { AppState, selectFullClient, selectFullCLientLoading } from "features";
import { Store } from "@ngrx/store";
import { SideNavComponent } from "@core/layout/components/side-nav/side-nav.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { StrutsDirective } from "@core/layout/components/struts/struts.directive";
import { filter } from "rxjs";
import { AppRoutesEnum, VersionRouteEnum } from "@core/layout/enums/layout-routes.enum";
import { HasFullAccessDirective } from "@core/directives/has-full-access.directive";
import { TopBarComponent } from "@core/layout/components/top-bar/top-bar.component";
import { LayoutService } from "@core/layout/services/layout.service";

// Application Core
// Shared Components and Modules
// Services
// import { ObservableUtils } from '@shared/utils/observable-utils.service';

// Feature Services
// UI Kit and Libraries
// Models and Enums
// Store Actions
// Store Selectors
@Component({
	selector: "cba-layout",
	templateUrl: "./layout.component.html",
	styleUrls: ["./layout.component.scss"],
	standalone: true,
	imports: [
		CommonModule,
		RouterOutlet,
		LayoutComponent,
		TopBarComponent,
		SideNavComponent,
		StrutsDirective,
		HasFullAccessDirective,
	],
})
export class AppLayoutComponent {
	private readonly store = inject(Store<AppState>);
	private readonly router = inject(Router);
	private readonly layoutService = inject(LayoutService);

	readonly $viewStruts = viewChild(StrutsDirective);

	readonly $fullClient = this.store.selectSignal(selectFullClient);
	readonly $loadingFullClient = this.store.selectSignal(selectFullCLientLoading);

	readonly $clientDroits = this.layoutService.$clientDroits;
	readonly $clientDroitsLoading = this.layoutService.$clientDroitsLoading;

	readonly $showLayout = this.layoutService.$emotionInited;

	constructor() {
		this.router.events
			.pipe(
				takeUntilDestroyed(),
				filter((event) => event instanceof NavigationEnd),
			)
			.subscribe(async () => {
				const emotion = await this.waitForElement("#app-emotion");
				const wantToDisplayStruts =
					document.location.href.indexOf([AppRoutesEnum.APP, VersionRouteEnum.V1].join("/")) > -1;
				const scrollbarElements = Array.from(
					document.getElementsByClassName("nicescroll-rails") as HTMLCollectionOf<HTMLElement>,
				);

				const angularStruts = this.$viewStruts()?.elementRef.nativeElement;

				// On prepare le contenu de page Struts dans Angular
				if (angularStruts && !angularStruts.childElementCount) {
					angularStruts.append(emotion);
				}

				if (!angularStruts) {
					document.body.append(emotion);
				}

				if (angularStruts) {
					if (wantToDisplayStruts) {
						this.$viewStruts().display();
					} else {
						this.$viewStruts().hide();
					}
				}
				if (emotion) emotion.style.display = wantToDisplayStruts ? "block" : "none";
				if (scrollbarElements) {
					for (const scrollbarElement of scrollbarElements) {
						scrollbarElement.style.display = wantToDisplayStruts ? "block" : "none";
					}
				}
			});
	}

	private waitForElement(selector: string) {
		return new Promise<HTMLElement>((resolve) => {
			if (document.querySelector(selector)) {
				return resolve(<HTMLElement>document.querySelector(selector));
			}

			const observer = new MutationObserver(() => {
				if (document.querySelector(selector)) {
					observer.disconnect();
					resolve(<HTMLElement>document.querySelector(selector));
				}
			});

			// If you get "parameter 1 is not of type 'Node'" error, see https://stackoverflow.com/a/77855838/492336
			observer.observe(document.body, {
				childList: true,
				subtree: true,
			});
		});
	}
}
